/**
 * MKS-393 — TanStack Query hooks for the merch size charts.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useErrorToast } from './queryUtils';
import {
  deleteMerchSizeChart,
  fetchMerchSizeChartUrl,
  fetchMerchSizeCharts,
  uploadMerchSizeChart,
} from '../services/api/merchSizeCharts';
import type { MerchChartItem } from '../constants/merchSizeCharts';

export const MERCH_SIZE_CHARTS_KEY = ['merch-size-charts'] as const;

/**
 * Which items have a chart. Read by /profile (to decide which "Size chart"
 * links exist at all) and by the HR card (to label each slot).
 */
export function useMerchSizeCharts() {
  return useQuery({
    queryKey: MERCH_SIZE_CHARTS_KEY,
    queryFn: fetchMerchSizeCharts,
    // A chart changes when HR uploads one, which is rare; the mutations below
    // invalidate this key, so a long stale time costs nothing.
    staleTime: 5 * 60 * 1000,
    select: (response) => response.data,
  });
}

/**
 * Fetch a signed URL on demand. A mutation rather than a query because it is
 * triggered by a click and its result expires — caching it would hand a
 * second click a URL that may already be dead.
 */
export function useMerchSizeChartUrl() {
  const onError = useErrorToast();
  return useMutation({
    mutationFn: (item: MerchChartItem) => fetchMerchSizeChartUrl(item),
    onError: (error) => onError(error, "Couldn't open the size chart"),
  });
}

export function useUploadMerchSizeChart() {
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: ({ item, file }: { item: MerchChartItem; file: File }) =>
      uploadMerchSizeChart(item, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: MERCH_SIZE_CHARTS_KEY });
    },
    onError: (error) => onError(error, "Couldn't upload the size chart"),
  });
}

export function useDeleteMerchSizeChart() {
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: (item: MerchChartItem) => deleteMerchSizeChart(item),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: MERCH_SIZE_CHARTS_KEY });
    },
    onError: (error) => onError(error, "Couldn't remove the size chart"),
  });
}
